import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getToken } from "functions/Auth";

let client = null;
let connected = false;
let subscriptions = new Map(); // topic -> callback
let isActivating = false;
let manuallyDisconnected = false;

const RECONNECT_DELAY = 2000;

const createClient = (url, onConnect, onError) => {
  const token = getToken();

  return new Client({
    webSocketFactory: () => new SockJS(`${url}?token=${token}`),

    connectHeaders: {
      Authorization: `Bearer ${token}`,
    },

    reconnectDelay: 0, // ❌ we handle reconnect manually

    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,

    debug: () => {},

    onConnect: (frame) => {
      connected = true;
      isActivating = false;
      manuallyDisconnected = false;

      console.log("✅ STOMP connected");

      // 🔄 Restore subscriptions
      subscriptions.forEach((callback, topic) => {
        client.subscribe(topic, callback);
      });

      onConnect?.(frame);
    },

    onStompError: (frame) => {
      console.error("❌ STOMP broker error:", frame);
      connected = false;
      onError?.(frame);
    },

    onWebSocketClose: () => {
      console.warn("🔌 WebSocket closed");
      connected = false;
      attemptReconnect(url, onConnect, onError);
    },

    onWebSocketError: (err) => {
      console.error("❌ WebSocket error", err);
      connected = false;
      attemptReconnect(url, onConnect, onError);
    },
  });
};

const attemptReconnect = (url, onConnect, onError) => {
  if (manuallyDisconnected || isActivating) return;

  console.log(`🔁 Reconnecting in ${RECONNECT_DELAY / 1000}s...`);
  isActivating = true;

  setTimeout(() => {
    if (manuallyDisconnected) return;

    client = createClient(url, onConnect, onError);
    client.activate();
  }, RECONNECT_DELAY);
};

export const stompService = {
  /**
   * Connect
   */
  connect: (url, onConnect, onError) => {
    if (client?.active || isActivating) return client;

    manuallyDisconnected = false;
    isActivating = true;

    client = createClient(url, onConnect, onError);
    client.activate();

    return client;
  },

  /**
   * Subscribe
   */
  subscribe: (topic, callback) => {
    if (!client || !connected) {
      console.warn("⚠️ STOMP not connected yet");
      return null;
    }

    const wrappedCallback = (message) => {
      try {
        callback(JSON.parse(message.body), message);
      } catch {
        callback(message.body, message);
      }
    };

    const subscription = client.subscribe(topic, wrappedCallback);

    subscriptions.set(topic, wrappedCallback);

    return () => {
      subscription.unsubscribe();
      subscriptions.delete(topic);
      console.log(`🧹 Unsubscribed from ${topic}`);
    };
  },

  /**
   * Send
   */
  send: (destination, body = {}) => {
    if (!client || !connected) {
      console.warn("⚠️ Cannot send — not connected");
      return;
    }

    client.publish({
      destination,
      body: typeof body === "string" ? body : JSON.stringify(body),
    });
  },

  /**
   * Manual disconnect (NO auto-reconnect)
   */
  disconnect: () => {
    // manuallyDisconnected = true;

    if (client) {
      client.deactivate();
      client = null;
    }

    connected = false;
    isActivating = false;
    subscriptions.clear();

    console.log("🔌 STOMP manually disconnected");
  },

  isConnected: () => connected,
};
